import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { campaignsAPI, templatesAPI } from '../services/api';
import { Target, Plus, Play, Square, Users, Trash2, CheckCircle2, AlertTriangle, Timer, Search, Grid, List, Copy, Edit, Beaker, ChevronRight } from 'lucide-react';
import SimpleCampaignForm from '../components/SimpleCampaignForm';
import CampaignTemplateEditor from '../components/CampaignTemplateEditor';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { campaignStatusClass } from './campaignStatus';
import { useOnboarding } from '../contexts/OnboardingContext';
import SetupErrorMessage from '../components/SetupErrorMessage';
import { mapSetupError } from '../utils/friendlyErrors';
import './CampaignsPage.css';

function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('campaignsViewMode') || 'tiles';
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { refresh: refreshOnboarding, step } = useOnboarding();
  const providerStep = step('email_provider');
  const senderStep = step('sender');
  const campaignStep = step('first_campaign');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm', variant: 'danger' });
  const [failureModal, setFailureModal] = useState({
    isOpen: false,
    loading: false,
    campaign: null,
    failures: [],
    error: ''
  });
  const [templateEditorModal, setTemplateEditorModal] = useState({
    isOpen: false,
    campaign: null,
    template: null,
    previewMode: false
  });
  const [previewTemplateOverrides, setPreviewTemplateOverrides] = useState(null);
  const [testModal, setTestModal] = useState({
    isOpen: false,
    campaign: null,
    testEmail: '',
    loading: false,
    templateInfo: null  // { provider, fromEmail }
  });

  const loadCampaigns = useCallback(async () => {
    try {
      const response = await campaignsAPI.getAll();
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setErrorMessage('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadCampaigns();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadCampaigns]);

  useEffect(() => {
    localStorage.setItem('campaignsViewMode', viewMode);
  }, [viewMode]);

  const formatTimestamp = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString();
    } catch (error) {
      return value;
    }
  };

  const handleViewFailures = async (campaign) => {
    setFailureModal({
      isOpen: true,
      loading: true,
      campaign,
      failures: [],
      error: ''
    });

    try {
      const response = await campaignsAPI.getDeliveryFailures(campaign.id);
      setFailureModal((prev) => ({
        ...prev,
        loading: false,
        failures: response.data.failures || [],
        error: ''
      }));
    } catch (error) {
      setFailureModal((prev) => ({
        ...prev,
        loading: false,
        error: error.response?.data?.error || 'Failed to load delivery failures'
      }));
    }
  };

  const closeFailureModal = () => {
    setFailureModal({
      isOpen: false,
      loading: false,
      campaign: null,
      failures: [],
      error: ''
    });
  };

  const summarizeFailureReason = (reason) => {
    if (!reason) return 'Unknown error';
    const normalized = reason.replace(/\s+/g, ' ').trim();
    return normalized.length > 140 ? `${normalized.slice(0, 140)}…` : normalized;
  };

  const handleCampaignCreateSuccess = async (campaignId) => {
    setShowCreateModal(false);
    setSuccessMessage('Campaign created successfully! You can now launch it.');
    setTimeout(() => setSuccessMessage(''), 5000);
    loadCampaigns();
    refreshOnboarding();
  };

  const handleCampaignUpdateSuccess = async (campaignId) => {
    setShowEditModal(false);
    setEditingCampaign(null);
    setSuccessMessage('Campaign updated successfully!');
    setTimeout(() => setSuccessMessage(''), 5000);
    loadCampaigns();

    // Optionally auto-launch the campaign
    // await handleLaunchSimpleCampaign(campaignId);
  };

  const buildLaunchMessage = (data) => {
    const scheduled = data.messages_scheduled || 0;
    const skipped = data.skipped_messages || 0;
    const sent = data.immediate_sent || 0;
    let msg = `Campaign launched! ${scheduled} message${scheduled !== 1 ? 's' : ''} scheduled`;
    if (sent > 0) msg += `, ${sent} sent immediately`;
    if (skipped > 0) {
      msg += `. ${skipped} skipped (no contact info on file)`;
      const failures = data.delivery_failures || [];
      const skippedNames = failures
        .filter(f => f.reason && f.reason.includes('missing'))
        .map(f => f.business_name || `Buyer #${f.buyer_id}`)
        .filter(Boolean)
        .slice(0, 5);
      if (skippedNames.length > 0) {
        msg += `: ${skippedNames.join(', ')}`;
        if (skipped > skippedNames.length) msg += ` +${skipped - skippedNames.length} more`;
      }
    }
    return msg;
  };

  const handleLaunchSimpleCampaign = async (campaignId) => {
    try {
      setLoading(true);
      const response = await campaignsAPI.launchSimple(campaignId);
      if (response.data.success) {
        refreshOnboarding();
        setSuccessMessage(buildLaunchMessage(response.data));
        setTimeout(() => setSuccessMessage(''), 8000);
        loadCampaigns();
      } else {
        setErrorMessage(response.data.error || 'Failed to launch campaign');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error launching campaign:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to launch campaign');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };


  const handleCampaignControl = async (campaignId, action) => {
    try {
      setLoading(true);

      if (action === 'launch') {
        // Check if campaign has a start_time in the past
        const campaign = campaigns.find(c => c.id === campaignId);
        if (campaign?.start_time) {
          const startTime = new Date(campaign.start_time);
          const now = new Date();

          if (startTime < now) {
            const minutesAgo = Math.round((now - startTime) / 1000 / 60);
            const timeAgoText = minutesAgo < 60
              ? `${minutesAgo} minute${minutesAgo !== 1 ? 's' : ''} ago`
              : `${Math.round(minutesAgo / 60)} hour${Math.round(minutesAgo / 60) !== 1 ? 's' : ''} ago`;

            setLoading(false);
            setConfirmDialog({
              isOpen: true,
              title: 'Start Time is in the Past',
              message: `This campaign was scheduled to start ${timeAgoText}. All messages may send immediately instead of being spaced out. Do you want to start from NOW instead?`,
              onConfirm: async () => {
                setLoading(true);
                try {
                  const response = await campaignsAPI.launch(campaignId);
                  if (response.data.success) {
                    refreshOnboarding();
                    setSuccessMessage(buildLaunchMessage(response.data));
                  } else {
                    setErrorMessage(response.data.error || 'Failed to launch campaign');
                  }
                  loadCampaigns();
                } catch (error) {
                  console.error('Error launching campaign:', error);
                  setErrorMessage(error.response?.data?.error || 'Failed to launch campaign');
                } finally {
                  setLoading(false);
                }
              },
              confirmText: 'Start from Now',
              variant: 'warning'
            });
            return;
          }
        }

        const response = await campaignsAPI.launch(campaignId);
        if (response.data.success) {
          refreshOnboarding();
          setSuccessMessage(buildLaunchMessage(response.data));
        } else {
          setErrorMessage(response.data.error || 'Failed to launch campaign');
        }
      } else if (action === 'stop') {
        const response = await campaignsAPI.stop(campaignId);
        if (response.data.success) {
          setSuccessMessage('Campaign stopped successfully');
        } else {
          setErrorMessage(response.data.error || 'Failed to stop campaign');
        }
      }

      setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 5000);
      loadCampaigns();
    } catch (error) {
      console.error('Error controlling campaign:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to control campaign');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (campaignId, campaignName) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Campaign',
      message: `Are you sure you want to delete "${campaignName}"? This action cannot be undone.`,
      onConfirm: () => handleDeleteConfirm(campaignId),
      confirmText: 'Delete',
      variant: 'danger'
    });
  };

  const handleDeleteConfirm = async (campaignId) => {
    try {
      setLoading(true);
      await campaignsAPI.delete(campaignId);
      setSuccessMessage('Campaign deleted successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
      loadCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to delete campaign');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateCampaign = async (campaignId, campaignName) => {
    try {
      setLoading(true);
      const response = await campaignsAPI.duplicate(campaignId);
      if (response.data.success) {
        const newCampaignId = response.data.campaign_id;
        // Reload the list, then open edit modal on the new copy so user can rename immediately
        await loadCampaigns();
        try {
          const newResp = await campaignsAPI.getById(newCampaignId);
          const newCampaign = newResp.data.campaign || newResp.data;
          handleEditCampaign(newCampaign);
        } catch {
          setSuccessMessage(`Campaign "${campaignName}" duplicated — find the copy in the list to rename it.`);
          setTimeout(() => setSuccessMessage(''), 6000);
        }
      } else {
        setErrorMessage(response.data.error || 'Failed to duplicate campaign');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error duplicating campaign:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to duplicate campaign');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleTestCampaign = async (campaign) => {
    setTestModal({
      isOpen: true,
      campaign: campaign,
      testEmail: '',
      loading: false,
      templateInfo: null
    });

    // Fetch template to show which provider/from-address will be used
    if (campaign.template_id) {
      try {
        const resp = await templatesAPI.getById(campaign.template_id);
        const tmpl = resp.data?.template || resp.data;
        const msgs = tmpl?.json_config?.messages || [];
        const first = msgs[0] || {};
        const provider = first.provider || tmpl?.json_config?.metadata?.emailProvider || 'smtp';
        const fromEmail = first.from_email || tmpl?.json_config?.metadata?.from_email || null;
        setTestModal(prev => ({
          ...prev,
          templateInfo: { provider, fromEmail }
        }));
      } catch (e) {
        // non-critical, just skip the info box
      }
    }
  };

  const handleSendTest = async () => {
    if (!testModal.testEmail || !testModal.testEmail.includes('@')) {
      setErrorMessage('Please enter a valid email address');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    try {
      setTestModal(prev => ({ ...prev, loading: true }));
      const response = await campaignsAPI.sendTest(testModal.campaign.id, testModal.testEmail);

      if (response.data.success) {
        const dbg = response.data.debug;
        const fromInfo = dbg ? ` (via ${dbg.provider}, from: ${dbg.from_email})` : '';
        const buyerInfo = dbg?.buyer
          ? dbg.buyer.using_real_buyer
            ? ` | Data: ${dbg.buyer.business_name}`
            : ` | Data: PLACEHOLDER (${dbg.buyer.reason})`
          : '';
        setSuccessMessage(`Test sent to ${testModal.testEmail}!${fromInfo}${buyerInfo}`);
        setTimeout(() => setSuccessMessage(''), 12000);
        setTestModal({ isOpen: false, campaign: null, testEmail: '', loading: false, templateInfo: null });
      } else {
        setErrorMessage(response.data.error || 'Failed to send test');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error sending test:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to send test');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setTestModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setShowEditModal(true);
  };

  const handleOpenTemplateEditorFromEdit = async () => {
    if (!editingCampaign) return;

    try {
      // Fetch the template data
      const response = await templatesAPI.getById(editingCampaign.template_id);
      setTemplateEditorModal({
        isOpen: true,
        campaign: editingCampaign,
        template: response.data.template || response.data,
        previewMode: false
      });
    } catch (error) {
      console.error('Error loading template:', error);
      setErrorMessage('Failed to load template for editing');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleOpenTemplateEditorFromCreate = async (templateId) => {
    try {
      // Fetch the template data
      const response = await templatesAPI.getById(templateId);
      setTemplateEditorModal({
        isOpen: true,
        campaign: null, // No campaign in create mode
        template: response.data.template || response.data,
        previewMode: true
      });
    } catch (error) {
      console.error('Error loading template:', error);
      setErrorMessage('Failed to load template for editing');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleSaveTemplateOverrides = async (templateOverrides) => {
    if (templateEditorModal.previewMode) {
      // Preview mode (create): just store the overrides, don't save to backend
      setPreviewTemplateOverrides(templateOverrides);
      setTemplateEditorModal({ isOpen: false, campaign: null, template: null, previewMode: false });
      return;
    }

    // Edit mode: save to backend
    try {
      const response = await campaignsAPI.updateTemplateOverrides(
        templateEditorModal.campaign.id,
        templateOverrides
      );
      if (response.data.success) {
        setSuccessMessage('Template changes saved successfully!');
        setTimeout(() => setSuccessMessage(''), 5000);

        // Update editingCampaign if it's the same campaign being edited
        if (editingCampaign && editingCampaign.id === templateEditorModal.campaign.id) {
          setEditingCampaign({
            ...editingCampaign,
            template_overrides: templateOverrides
          });
        }

        loadCampaigns();
        setTemplateEditorModal({ isOpen: false, campaign: null, template: null, previewMode: false });
      } else {
        setErrorMessage(response.data.error || 'Failed to save template changes');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error saving template overrides:', error);
      throw error; // Re-throw so the modal can handle it
    }
  };

  // The campaign name is a real link stretched over the whole card / row (CSS ::after).
  // A table row is not a positioning context in every browser, so a click on the row
  // itself still opens the campaign; clicks on the link, buttons or actions do not.
  const handleRowClick = (event, campaignId) => {
    if (event.target.closest('a, button, .cmp-actions')) return;
    navigate(`/campaigns/${campaignId}`);
  };

  const getFailedCount = (campaign) =>
    campaign.actual_failed_count ?? (campaign.total_failed || 0) + (campaign.total_skipped || 0);

  const formatShortDate = (value) =>
    new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const formatScheduleDate = (value) =>
    new Date(value).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="campaigns-page">
      <div className="page-header cmp-page-header">
        <div className="header-content">
          <h1 className="page-title">
            <Target className="title-icon" />
            Campaign Manager
          </h1>
          <p className="page-description">
            Create and manage multi-channel outreach campaigns
          </p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus className="cmp-btn-icon" />
            Create Campaign
          </button>
        </div>
      </div>

      {providerStep && providerStep.status !== 'done' ? (
        <div className="alert alert-warning cmp-setup-hint">
          Connect an email provider before launching a campaign.{' '}
          <Link to="/settings#resend">Open Settings</Link>
        </div>
      ) : senderStep && senderStep.status !== 'done' && (senderStep.status === 'blocked' || campaignStep?.blocked_by === 'sender') && (
        <div className="alert alert-warning cmp-setup-hint">
          {senderStep.description}{' '}
          <Link to="/settings#sender">Open Settings</Link>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-error">
          <SetupErrorMessage message={errorMessage} />
        </div>
      )}

      <div className="filters-section">
        <div className="search-bar">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="view-toggle">
          <button
            className={`view-toggle-btn ${viewMode === 'tiles' ? 'active' : ''}`}
            onClick={() => setViewMode('tiles')}
            title="Tiles View"
          >
            <Grid size={18} />
          </button>
          <button
            className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List View"
          >
            <List size={18} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading campaigns...</p>
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="empty-state">
          <Target className="empty-icon" />
          <h3>{campaigns.length === 0 ? 'No campaigns yet' : 'No campaigns found'}</h3>
          <p>{campaigns.length === 0 ? 'Create your first campaign to get started' : 'Try adjusting your search'}</p>
          {campaigns.length === 0 && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus className="cmp-btn-icon" />
              Create Campaign
            </button>
          )}
        </div>
      ) : viewMode === 'list' ? (
        <div className="cmp-table-card">
          <div className="cmp-table-wrap">
            <table className="cmp-table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Template / Group</th>
                  <th>Progress</th>
                  <th>Scheduled</th>
                  <th>Created</th>
                  <th className="cmp-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.map((campaign) => {
                  const sent = campaign.total_sent || 0;
                  const contacts = campaign.total_contacts || 0;
                  const failed = getFailedCount(campaign);
                  const progressPct = contacts > 0 ? Math.min(100, Math.round((sent / contacts) * 100)) : 0;
                  const scheduledAt = campaign.start_time || (campaign.status === 'scheduled' ? campaign.scheduled_time : null);

                  return (
                    <tr
                      key={campaign.id}
                      className="cmp-row"
                      onClick={(e) => handleRowClick(e, campaign.id)}
                    >
                      <td>
                        <div className="cmp-cell-name">
                          <Link to={`/campaigns/${campaign.id}`} className="cmp-row-name cmp-stretched-link">
                            {campaign.name}
                          </Link>
                          <span className={campaignStatusClass(campaign.status)}>{campaign.status}</span>
                        </div>
                      </td>
                      <td>
                        <div className="cmp-cell-stack">
                          <span className="cmp-cell-main">{campaign.template_name || '—'}</span>
                          <span className="cmp-cell-sub">
                            {campaign.group_name || 'No group'}
                            {campaign.template_provider && <> · via {campaign.template_provider}</>}
                          </span>
                          {campaign.template_from_email && (
                            <span className="cmp-cell-sub">{campaign.template_from_email}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="cmp-progress">
                          <div className="cmp-progress-text">
                            <span className="cmp-num">{sent}</span>
                            <span className="cmp-cell-sub"> / {contacts} sent</span>
                            {failed > 0 && <span className="cmp-failed-inline">{failed} failed</span>}
                          </div>
                          <div className="cmp-progress-track">
                            <div className="cmp-progress-fill" style={{ width: `${progressPct}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="cmp-cell-muted">
                        {scheduledAt ? formatScheduleDate(scheduledAt) : 'Immediately'}
                      </td>
                      <td className="cmp-cell-muted">
                        {formatShortDate(campaign.created_at)}
                      </td>
                      <td className="cmp-td-actions">
                        <div className="cmp-actions">
                          {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                            <button
                              type="button"
                              className="cmp-icon-btn"
                              title="Launch Campaign"
                              aria-label="Launch Campaign"
                              onClick={() => handleCampaignControl(campaign.id, 'launch')}
                            >
                              <Play size={15} />
                            </button>
                          )}
                          {campaign.status === 'running' && (
                            <button
                              type="button"
                              className="cmp-icon-btn"
                              title="Stop Campaign"
                              aria-label="Stop Campaign"
                              onClick={() => handleCampaignControl(campaign.id, 'stop')}
                            >
                              <Square size={15} />
                            </button>
                          )}
                          {campaign.status !== 'running' && (
                            <button
                              type="button"
                              className="cmp-icon-btn"
                              title="Edit Campaign"
                              aria-label="Edit Campaign"
                              onClick={() => handleEditCampaign(campaign)}
                            >
                              <Edit size={15} />
                            </button>
                          )}
                          <button
                            type="button"
                            className="cmp-icon-btn"
                            title="Duplicate Campaign"
                            aria-label="Duplicate Campaign"
                            onClick={() => handleDuplicateCampaign(campaign.id, campaign.name)}
                          >
                            <Copy size={15} />
                          </button>
                          <button
                            type="button"
                            className="cmp-icon-btn"
                            title="Test Campaign"
                            aria-label="Test Campaign"
                            onClick={() => handleTestCampaign(campaign)}
                          >
                            <Beaker size={15} />
                          </button>
                          <button
                            type="button"
                            className="cmp-icon-btn cmp-icon-btn--delete"
                            title="Delete Campaign"
                            aria-label="Delete Campaign"
                            onClick={() => handleDeleteClick(campaign.id, campaign.name)}
                          >
                            <Trash2 size={15} />
                          </button>
                          <ChevronRight className="cmp-row-chevron" aria-hidden="true" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="cmp-grid">
          {filteredCampaigns.map((campaign) => {
            const templateLabel = campaign.template_name
              || (campaign.campaign_type ? campaign.campaign_type.replace(/_/g, ' ') : 'Template campaign');
            const groupLabel = campaign.group_name || (campaign.group_id ? `Group #${campaign.group_id}` : 'No group');
            const friendlyDescription = campaign.description || 'This campaign will use your selected template to contact group members.';
            const failed = getFailedCount(campaign);
            const scheduledAt = campaign.start_time || (campaign.status === 'scheduled' ? campaign.scheduled_time : null);

            return (
            <div key={campaign.id} className="cmp-card">
              <div className="cmp-card-top">
                <div className="cmp-card-heading">
                  <div className="cmp-card-title-row">
                    <h3 className="cmp-card-name">
                      <Link to={`/campaigns/${campaign.id}`} className="cmp-card-link cmp-stretched-link">
                        {campaign.name}
                      </Link>
                    </h3>
                    <span className={campaignStatusClass(campaign.status)}>{campaign.status}</span>
                  </div>
                  <div className="cmp-card-meta">
                    <span>{templateLabel}</span>
                    <span className="cmp-dot" aria-hidden="true">·</span>
                    <span>{groupLabel}</span>
                    {campaign.template_provider && (
                      <>
                        <span className="cmp-dot" aria-hidden="true">·</span>
                        <span>via {campaign.template_provider}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="cmp-actions">
                  {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                    <button
                      type="button"
                      className="cmp-icon-btn"
                      title="Launch Campaign"
                      aria-label="Launch Campaign"
                      onClick={() => handleCampaignControl(campaign.id, 'launch')}
                    >
                      <Play size={15} />
                    </button>
                  )}
                  {campaign.status === 'running' && (
                    <button
                      type="button"
                      className="cmp-icon-btn"
                      title="Stop Campaign"
                      aria-label="Stop Campaign"
                      onClick={() => handleCampaignControl(campaign.id, 'stop')}
                    >
                      <Square size={15} />
                    </button>
                  )}
                  {campaign.status !== 'running' && (
                    <button
                      type="button"
                      className="cmp-icon-btn"
                      title="Edit Campaign"
                      aria-label="Edit Campaign"
                      onClick={() => handleEditCampaign(campaign)}
                    >
                      <Edit size={15} />
                    </button>
                  )}
                  <button
                    type="button"
                    className="cmp-icon-btn"
                    title="Duplicate Campaign"
                    aria-label="Duplicate Campaign"
                    onClick={() => handleDuplicateCampaign(campaign.id, campaign.name)}
                  >
                    <Copy size={15} />
                  </button>
                  <button
                    type="button"
                    className="cmp-icon-btn"
                    title="Test Campaign"
                    aria-label="Test Campaign"
                    onClick={() => handleTestCampaign(campaign)}
                  >
                    <Beaker size={15} />
                  </button>
                  <button
                    type="button"
                    className="cmp-icon-btn cmp-icon-btn--delete"
                    title="Delete Campaign"
                    aria-label="Delete Campaign"
                    onClick={() => handleDeleteClick(campaign.id, campaign.name)}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              <p className="cmp-card-desc">{friendlyDescription}</p>

              <dl className="cmp-kv">
                <div className="cmp-kv-item">
                  <dt>Sending</dt>
                  <dd>
                    {campaign.template_from_email
                      ? campaign.template_from_email
                      : <span className="cmp-kv-empty">Default address from settings</span>}
                  </dd>
                </div>
                <div className="cmp-kv-item">
                  <dt>Scheduled</dt>
                  <dd>{scheduledAt ? new Date(scheduledAt).toLocaleString() : 'Immediately'}</dd>
                </div>
              </dl>

              <div className="cmp-stats">
                <div className="cmp-stat">
                  <span className="cmp-stat-label"><Users className="cmp-stat-icon" aria-hidden="true" />Contacts</span>
                  <span className="cmp-stat-value">{campaign.total_contacts || 0}</span>
                </div>
                <div className="cmp-stat">
                  <span className="cmp-stat-label"><CheckCircle2 className="cmp-stat-icon" aria-hidden="true" />Sent</span>
                  <span className="cmp-stat-value">{campaign.total_sent || 0}</span>
                </div>
                <div className="cmp-stat">
                  <span className="cmp-stat-label"><AlertTriangle className="cmp-stat-icon" aria-hidden="true" />Failed</span>
                  <span className={`cmp-stat-value${failed > 0 ? ' cmp-stat-value--danger' : ''}`}>{failed}</span>
                  <button
                    type="button"
                    className="cmp-stat-link"
                    onClick={() => handleViewFailures(campaign)}
                  >
                    View details
                  </button>
                </div>
                <div className="cmp-stat">
                  <span className="cmp-stat-label"><Timer className="cmp-stat-icon" aria-hidden="true" />Scheduled</span>
                  <span className="cmp-stat-value">{campaign.total_scheduled || 0}</span>
                </div>
              </div>

              <div className="cmp-card-foot">
                <span className="cmp-card-date">Created {formatShortDate(campaign.created_at)}</span>
                <span className="cmp-card-view" aria-hidden="true">
                  View
                  <ChevronRight className="cmp-card-view-icon" />
                </span>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <Modal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setPreviewTemplateOverrides(null);
          }}
          title="Create Campaign"
        >
          <SimpleCampaignForm
            onClose={() => {
              setShowCreateModal(false);
              setPreviewTemplateOverrides(null);
            }}
            onSuccess={handleCampaignCreateSuccess}
            onOpenTemplateEditor={handleOpenTemplateEditorFromCreate}
            previewTemplateOverrides={previewTemplateOverrides}
          />
        </Modal>
      )}

      {/* Edit Campaign Modal */}
      {showEditModal && editingCampaign && (
        <Modal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingCampaign(null);
          }}
          title="Edit Campaign"
        >
          <SimpleCampaignForm
            campaign={editingCampaign}
            onClose={() => {
              setShowEditModal(false);
              setEditingCampaign(null);
            }}
            onSuccess={handleCampaignUpdateSuccess}
            onOpenTemplateEditor={handleOpenTemplateEditorFromEdit}
          />
        </Modal>
      )}

      {/* Edit Campaign Template Modal */}
      <CampaignTemplateEditor
        isOpen={templateEditorModal.isOpen}
        onClose={() => setTemplateEditorModal({ isOpen: false, campaign: null, template: null, previewMode: false })}
        campaign={templateEditorModal.campaign}
        template={templateEditorModal.template}
        onSave={handleSaveTemplateOverrides}
        previewMode={templateEditorModal.previewMode}
      />

      {failureModal.isOpen && (
        <Modal
          isOpen={failureModal.isOpen}
          onClose={closeFailureModal}
          title={`Delivery issues – ${failureModal.campaign?.name || ''}`}
          size="xlarge"
        >
          {failureModal.loading ? (
            <div className="failure-modal-loading">Loading failures...</div>
          ) : failureModal.error ? (
            <div className="alert alert-error">{failureModal.error}</div>
          ) : failureModal.failures.length === 0 ? (
            <p className="cmp-modal-empty">No failed or skipped deliveries recorded for this campaign.</p>
          ) : (
            <div className="failures-table">
              <div className="failures-table-header">
                <span>Buyer</span>
                <span>Channel</span>
                <span>Reason</span>
                <span className="failures-col-time">Timestamp</span>
              </div>
              {failureModal.failures.map((failure) => (
                <div key={failure.id} className="failures-row">
                  <div>
                    <strong>{failure.buyer_name || `Buyer #${failure.buyer_id}`}</strong>
                    <div className="failures-row-subtext">
                      {failure.email || failure.phone || 'No contact info'}
                    </div>
                  </div>
                  <div>
                    <span className="failures-chip">{(failure.contact_type || '').toUpperCase()}</span>
                  </div>
                  <div className="failures-reason-cell">
                    <div className="failures-reason" title={failure.reason || 'Unknown error'}>
                      {mapSetupError(failure.reason)
                        ? <SetupErrorMessage message={failure.reason} />
                        : summarizeFailureReason(failure.reason)}
                    </div>
                    {failure.message_subject && (
                      <div className="failures-row-subtext failures-clamp" title={failure.message_subject}>
                        Subject: {failure.message_subject}
                      </div>
                    )}
                  </div>
                  <div className="failures-row-subtext failures-col-time">
                    {formatTimestamp(failure.sent_at || failure.scheduled_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* Test Campaign Modal */}
      {testModal.isOpen && (
        <Modal
          isOpen={testModal.isOpen}
          onClose={() => setTestModal({ isOpen: false, campaign: null, testEmail: '', loading: false, templateInfo: null })}
          title={`Test Campaign – ${testModal.campaign?.name || ''}`}
        >
          <div className="cmp-test-body">
            <p className="cmp-test-intro">
              Send a test of this campaign to a specific email address. The campaign will run as normal but only for this single recipient.
            </p>
            {/* Show what provider/from-address is configured in the template */}
            {testModal.templateInfo ? (
              <div className="cmp-test-info">
                <span><strong>Provider:</strong> {testModal.templateInfo.provider}</span>
                <span><strong>From:</strong> {testModal.templateInfo.fromEmail || <em className="cmp-test-info-empty">default from settings</em>}</span>
              </div>
            ) : testModal.campaign?.template_id ? (
              <div className="cmp-test-info-placeholder" />
            ) : null}
            <div className="form-group">
              <label htmlFor="test-email">Test Email Address *</label>
              <input
                id="test-email"
                type="email"
                className="cmp-input"
                value={testModal.testEmail}
                onChange={(e) => setTestModal(prev => ({ ...prev, testEmail: e.target.value }))}
                placeholder="test@example.com"
                disabled={testModal.loading}
              />
            </div>
            <div className="cmp-modal-foot">
              <button
                className="btn btn-secondary"
                onClick={() => setTestModal({ isOpen: false, campaign: null, testEmail: '', loading: false, templateInfo: null })}
                disabled={testModal.loading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSendTest}
                disabled={testModal.loading || !testModal.testEmail}
              >
                {testModal.loading ? 'Sending...' : 'Send Test'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm', variant: 'danger' })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
      />
    </div>
  );
}

export default CampaignsPage;
